import { useEffect, useRef, useState } from "react";
import "../TrainerDashboard.css";
import {
  getMcqsByCourse,
  deleteMcq,
  addMcq,
  updateMcq,
  getMcqById,
} from "../../../../services/McqService";
import { getMe } from "../../../../services/AuthService";
import { getTrainers, getTrainerByUserId } from "../../../../services/TrainerService";
import Swal from "sweetalert2";

import { ArrowLeft, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import TextInput from "../../../components/common/FormFields/TextInput.jsx";
import NewCustomAsyncSelectDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import { mcqStepComplete } from "./mcqSteps.js";
import { toast } from '../../common/Toast/toast';
import { useAuth } from "../../../../context/AuthContext";
import { confirm } from '../../common/Confirm/confirm';
import PopupDone from '../../common/PopupDone/PopupDone';

// How many trainers a page of the picker holds.
const TRAINER_PAGE = 20;

// Add and edit ask the same three things in turn.
const MCQ_FORM_STEPS = [
  { label: "Question", title: "What is the question?", hint: "Write the question as students will read it." },
  { label: "Answers", title: "What are the options?", hint: "Four options, and which of them is right." },
  { label: "Trainer", title: "Who is it for?", hint: "Confirm the trainer this question belongs to." },
];

const McqList = ({
  courseId,
  courseName,
  onBack,
  selectedTrainerFilter,
  // Shown inside the course dialog rather than as a page of its own,
  // which already draws the heading and the way back.
  embedded = false,
}) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMcq, setEditingMcq] = useState(null);
  // The form dialog's three steps, with the same trailing-step exit the
  // attendance form uses.
  // What the form dialog says after a submit.
  const [formNotice, setFormNotice] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [formShownStep, setFormShownStep] = useState(1);
  const [formPhase, setFormPhase] = useState("in");

  useEffect(() => {
    if (formShownStep === formStep) return undefined;
    setFormPhase("out");
    const t = setTimeout(() => {
      setFormShownStep(formStep);
      setFormPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [formStep, formShownStep]);

  // Opening the form, whether to add or to edit, starts at the first
  // question again.
  useEffect(() => {
    if (showAddForm) {
      setFormStep(1);
      setFormShownStep(1);
      setFormNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [trainerHasMore, setTrainerHasMore] = useState(true);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [trainerSearch, setTrainerSearch] = useState("");
  // The trainer on the record being edited. It may live on a page of the
  // list that has not been fetched, so the control is told about it
  // directly rather than looking it up in what happens to be loaded.
  const [selectedTrainerOption, setSelectedTrainerOption] = useState(null);
  const [trainerId, setTrainerId] = useState("");
  const [actualTrainerId, setActualTrainerId] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [effectiveTrainerFilter, setEffectiveTrainerFilter] = useState("");
  const [trainerIdMapping, setTrainerIdMapping] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const recordsPerPage = 20;
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const mcqEndRef = useRef(null);
  const navigate = useNavigate();
  const trainersLoadedRef = useRef(false);
  // Which trainer fetch is the current one, and how far the list has got.
  const trainerSeqRef = useRef(0);
  const trainerPageRef = useRef(1);
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!hasPermission("course-wise-mcq", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view MCQs.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const fetchTrainerObjectId = async (userId) => {
    if (!userId) return null;
    if (trainerIdMapping[userId]) return trainerIdMapping[userId];
    
    try {
      const response = await getTrainerByUserId(userId);
      const trainerData = response.data;
      const trainerObjId = trainerData.id || trainerData._id;
      if (trainerObjId) {
        setTrainerIdMapping(prev => ({ ...prev, [userId]: trainerObjId }));
        return trainerObjId;
      }
      return userId;
    } catch (err) {
      console.error("Error fetching trainer object ID:", err);
      return userId;
    }
  };

  const toTrainerOption = (trainer) => {
    if (!trainer) return null;
    const trainerUserId = trainer.userId || trainer.trainerId || trainer.id;
    if (!trainerUserId) return null;
    const name = trainer.trainerName || "";
    const email = trainer.trainerEmail || trainer.traineremail || "";
    const label = email
      ? `${name} (${email})`
      : name || `Trainer ${trainerUserId}`;
    return {
      value: trainerUserId,
      label,
      email,
      trainerObjectId: trainer.id || trainer._id,
    };
  };

  /* Trainers arrive a page at a time, searched on the server and extended
     as the menu is scrolled -- the same way the filter above this list
     loads them. Every trainer used to be pulled in one go before the
     dialog could be used, so the picker showed whatever that single walk
     came back with and nothing more. Nothing here narrows the list to the
     course: any trainer can be the one a question belongs to. */
  const loadTrainers = async (page = 1, search = "") => {
    const req = (trainerSeqRef.current += 1);
    setTrainerLoading(true);
    try {
      const resp = await getTrainers(TRAINER_PAGE, page, search);
      // A newer search has taken over; this answer is stale.
      if (trainerSeqRef.current !== req) return;
      const list = Array.isArray(resp?.data) ? resp.data : [];
      const options = list.map(toTrainerOption).filter(Boolean);

      // Guarded by id so a repeated page cannot double up an option.
      setTrainerOptions((prev) => {
        if (page === 1) return options;
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...options.filter((o) => !seen.has(o.value))];
      });
      setTrainerIdMapping((prev) => {
        const next = { ...prev };
        options.forEach((opt) => {
          if (opt.value && opt.trainerObjectId) {
            next[opt.value] = opt.trainerObjectId;
          }
        });
        return next;
      });

      const hasNext = resp?.pagination?.hasNext;
      setTrainerHasMore(
        typeof hasNext === "boolean" ? hasNext : list.length === TRAINER_PAGE,
      );
      trainerPageRef.current = page;
    } catch (error) {
      console.error("Error fetching trainers:", error);
      if (trainerSeqRef.current === req) {
        toast.error(`Error loading trainers: ${error.message}`);
      }
    } finally {
      if (trainerSeqRef.current === req) setTrainerLoading(false);
    }
  };

  const fetchTrainersDirectly = async () => {
    if (trainersLoadedRef.current) return;
    trainersLoadedRef.current = true;
    await loadTrainers(1, "");
  };

  const handleTrainerMenuOpen = () => {
    if (trainersLoadedRef.current) return;
    trainersLoadedRef.current = true;
    loadTrainers(1, "");
  };

  const handleTrainerSearch = (value) => {
    trainersLoadedRef.current = true;
    setTrainerSearch(value);
    setTrainerHasMore(true);
    trainerPageRef.current = 1;
    loadTrainers(1, value);
  };

  const handleTrainerScrollEnd = () => {
    if (!trainerHasMore || trainerLoading) return;
    loadTrainers(trainerPageRef.current + 1, trainerSearch);
  };

  const handleTrainerRefresh = () => {
    trainersLoadedRef.current = true;
    setTrainerSearch("");
    setTrainerHasMore(true);
    setTrainerOptions([]);
    trainerPageRef.current = 1;
    loadTrainers(1, "");
  };

  const validationSchema = Yup.object().shape({
    question: Yup.string()
      .required("Question is required")
      .min(10, "Question must be at least 10 characters")
      .max(500, "Question cannot exceed 500 characters"),
    option1: Yup.string().required("Option 1 is required"),
    option2: Yup.string().required("Option 2 is required"),
    option3: Yup.string().required("Option 3 is required"),
    option4: Yup.string().required("Option 4 is required"),
    correctAnswer: Yup.number()
      .required("Correct answer is required")
      .oneOf([1, 2, 3, 4], "Please select a valid option (1-4)"),
    selectedTrainer: isAdmin
      ? Yup.string().required("Please select a trainer")
      : Yup.string(),
  });

  const getInitialValues = () => {
    if (editingMcq) {
      let trainerValue = "";
      if (isTrainer) {
        trainerValue = actualTrainerId;
      } else {
        if (editingMcq.selectedTrainer) {
          trainerValue = editingMcq.selectedTrainer;
        } else if (editingMcq.trainerId) {
          if (typeof editingMcq.trainerId === 'string') {
            trainerValue = editingMcq.trainerId;
          } else if (Array.isArray(editingMcq.trainerId) && editingMcq.trainerId.length > 0) {
            trainerValue = editingMcq.trainerId[0].userId || editingMcq.trainerId[0].id;
          }
        }
      }
      
      return {
        question: editingMcq.question || "",
        option1: editingMcq.option1 || "",
        option2: editingMcq.option2 || "",
        option3: editingMcq.option3 || "",
        option4: editingMcq.option4 || "",
        correctAnswer: editingMcq.correctAnswer || "",
        selectedTrainer: trainerValue,
      };
    }
    return {
      question: "",
      option1: "",
      option2: "",
      option3: "",
      option4: "",
      correctAnswer: "",
      selectedTrainer: isTrainer ? actualTrainerId : "",
    };
  };

  const getCurrentTrainerName = () => {
    if (!currentUser) return "";
    const firstName = currentUser.firstName || "";
    const lastName = currentUser.lastName || "";
    const email = currentUser.email || "";

    if (firstName || lastName) {
      let name = `${firstName} ${lastName}`.trim();
      if (email) {
        name += ` (${email})`;
      }
      return name;
    }
    return email;
  };

  const fetchData = async (page = 1, search = "") => {
    // Read once, before anything can await -- see the note where it is
    // used below.
    const isAppend = appendRef.current;
    try {
      if (!courseId) return;
      
      // Don't fetch if we don't have the effective trainer filter yet
      if (isAdmin && !effectiveTrainerFilter && selectedTrainerFilter) {
        console.log("Waiting for effective trainer filter to be set...");
        return;
      }
      
      setLoading(true);
      const response = await getMcqsByCourse(
        courseId,
        recordsPerPage,
        page,
        search,
        effectiveTrainerFilter,
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      // Captured before the request, not read live -- see the note in
      // DisplayMcqCourse: a ref reset mid-flight turned an append into a
      // replace and snapped the list back.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...rows.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(rows);
      }
      appendRef.current = false;
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setTotalRecords(response?.pagination?.total ?? rows.length);
    } catch (error) {
      console.error("Error fetching MCQs:", error);
      toast.error("Failed to fetch MCQs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const initializeComponent = async () => {
    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = userData.id || userData._id || userData.userId;

      if (!userId) {
        toast.error("Unable to fetch user information");
        setLoading(false);
        return;
      }

      setCurrentUser(userData);

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser = profileLabel === "admin" || userData.isSuperAdmin;

      setIsTrainer(isTrainerUser);
      setIsAdmin(isAdminUser);

      let filterValue = "";
      
      if (isTrainerUser) {
        try {
          const trainerResponse = await getTrainerByUserId(userId);
          const trainerData = trainerResponse.data;
          const actualTrainerObjectId = trainerData.id || trainerData._id;
          
          if (actualTrainerObjectId) {
            setActualTrainerId(actualTrainerObjectId);
            setTrainerId(actualTrainerObjectId);
            filterValue = actualTrainerObjectId;
          } else {
            setActualTrainerId(userId);
            setTrainerId(userId);
            filterValue = userId;
          }
        } catch (err) {
          console.error("Error fetching trainer data:", err);
          setActualTrainerId(userId);
          setTrainerId(userId);
          filterValue = userId;
        }
      } else if (isAdminUser && selectedTrainerFilter) {
        // For admin, selectedTrainerFilter already contains the actual trainer object ID from parent
        filterValue = selectedTrainerFilter;
      }
      
      // Set the effective trainer filter
      setEffectiveTrainerFilter(filterValue);

      if (isAdminUser) {
        await fetchTrainersDirectly();
      }

      // Mark as initialized
      setIsInitialized(true);
      
    } catch (err) {
      console.error("Error initializing component:", err);
      toast.error("Failed to initialize component");
      setLoading(false);
    }
  };

  // Initialization effect
  useEffect(() => {
    initializeComponent();
  }, [courseId]);

  // Separate effect for data fetching after initialization and when dependencies change
  useEffect(() => {
    // Only fetch data when:
    // 1. Component is initialized
    // 2. We have a courseId
    // 3. We have the effective trainer filter (for admin) OR we're a trainer
    // 4. Not in loading state
    
    if (!isInitialized) {
      return;
    }
    
    if (!courseId) {
      return;
    }
    
    // For admin, wait until effectiveTrainerFilter is set
    if (isAdmin && !effectiveTrainerFilter && selectedTrainerFilter) {
      // If we have selectedTrainerFilter from props but effectiveTrainerFilter isn't set yet,
      // we need to set it
      setEffectiveTrainerFilter(selectedTrainerFilter);
      return;
    }
    
    // For admin without selectedTrainerFilter (viewing all MCQs), effectiveTrainerFilter should be empty
    // For trainer, effectiveTrainerFilter should already be set
    
    // Fetch data
    const timeoutId = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 100);
    
    return () => clearTimeout(timeoutId);
    
  }, [
    isInitialized,
    currentPage,
    searchTerm,
    courseId,
    effectiveTrainerFilter,
    isAdmin,
    selectedTrainerFilter
  ]);

  // Effect to handle when selectedTrainerFilter changes from props
  useEffect(() => {
    if (isAdmin && selectedTrainerFilter !== undefined && isInitialized) {
      if (effectiveTrainerFilter !== selectedTrainerFilter) {
        setEffectiveTrainerFilter(selectedTrainerFilter);
        // Reset to first page when trainer filter changes
        setCurrentPage(1);
      }
    }
  }, [selectedTrainerFilter, isAdmin, isInitialized]);

  const handleAddMcq = () => {
    setEditingMcq(null);
    setSelectedTrainerOption(null);
    setShowAddForm(true);
  };

  const handleEditMcq = async (mcqId) => {
    try {
      const response = await getMcqById(mcqId);
      if (response.data) {
        let trainerIdValue = "";
        if (response.data.trainerId && Array.isArray(response.data.trainerId) && response.data.trainerId.length > 0) {
          trainerIdValue = response.data.trainerId[0].userId || response.data.trainerId[0].id;
        } else if (response.data.trainerId && !Array.isArray(response.data.trainerId)) {
          trainerIdValue = response.data.trainerId;
        }
        
        const mcqData = {
          ...response.data,
          trainerId: trainerIdValue,
          selectedTrainer: trainerIdValue,
          correctAnswer: response.data.correctAnswer
        };

        // The record carries its trainer, so the control can show it
        // whether or not that trainer is on the page of the list that
        // happens to be loaded.
        const trainerRecord = Array.isArray(response.data.trainerId)
          ? response.data.trainerId[0]
          : null;
        setSelectedTrainerOption(
          toTrainerOption(trainerRecord) ||
            (trainerIdValue
              ? { value: trainerIdValue, label: "Selected trainer" }
              : null),
        );

        setEditingMcq(mcqData);
        setShowAddForm(true);
      } else {
        toast.error("MCQ not found!");
      }
    } catch (error) {
      console.error("Error fetching MCQ details:", error);
      toast.error("Failed to load MCQ details");
    }
  };

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setFormSubmitting(true);

      let trainerIdToUse = "";
      if (isTrainer) {
        trainerIdToUse = actualTrainerId;
      } else if (isAdmin) {
        const selectedUserId = values.selectedTrainer;
        if (selectedUserId) {
          if (trainerIdMapping[selectedUserId]) {
            trainerIdToUse = trainerIdMapping[selectedUserId];
          } else {
            trainerIdToUse = await fetchTrainerObjectId(selectedUserId);
          }
        }
      }

      if (!trainerIdToUse) {
        toast.error("Trainer ID is required");
        return;
      }

      const correctAnswerNumber = parseInt(values.correctAnswer, 10);

      const mcqData = {
        question: values.question,
        option1: values.option1,
        option2: values.option2,
        option3: values.option3,
        option4: values.option4,
        correctAnswer: correctAnswerNumber,
        courseIds: courseId,
        trainerId: trainerIdToUse,
      };
      
      const wasEditing = Boolean(editingMcq);
      if (wasEditing) {
        await updateMcq(mcqData, editingMcq.id);
      } else {
        await addMcq(mcqData);
      }

      // Said in the dialog the work was done in, not in a corner toast.
      setFormNotice({
        kind: "ok",
        text: wasEditing ? "Question updated." : "Question added.",
      });
      // The dialog stays up. An added question's fields are emptied for
      // the next one; an edited one keeps showing what was saved.
      if (!wasEditing) resetForm();
      appendRef.current = false;
      fetchData(1, searchTerm);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error processing MCQ:", error);
      if (error.response && error.response.status === 409) {
        toast.error("This MCQ already exists in this course!");
      } else {
        toast.error("Error processing MCQ!");
      }
    } finally {
      setFormSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingMcq(null);
  };

  const handleDelete = async (id) => {
    confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this MCQ?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      tone: 'danger',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteMcq(id);
          fetchData(currentPage, searchTerm);
          toast.success("MCQ deleted successfully!");
        } catch (error) {
          toast.error("Failed to delete the MCQ. Please try again.");
        }
      }
    });
  };

  const handleView = (id) => {
    const mcq = data.find((item) => item.id === id);
    if (!mcq) {
      toast.error("MCQ not found.");
      return;
    }

    let answerDisplay = mcq.correctAnswer || "N/A";
    switch (mcq.correctAnswer) {
      case 1:
        answerDisplay = mcq.option1;
        break;
      case 2:
        answerDisplay = mcq.option2;
        break;
      case 3:
        answerDisplay = mcq.option3;
        break;
      case 4:
        answerDisplay = mcq.option4;
        break;
    }

    Swal.fire({
      html: `
      <div style="text-align: left;">
        <p><strong>Question:</strong> ${mcq.question || "N/A"}</p>
        <div style="margin: 15px 0;">
          <p><strong>Options:</strong></p>
          <div style="margin-left: 20px;">
            <p><strong>A:</strong> ${mcq.option1 || "N/A"}</p>
            <p><strong>B:</strong> ${mcq.option2 || "N/A"}</p>
            <p><strong>C:</strong> ${mcq.option3 || "N/A"}</p>
            <p><strong>D:</strong> ${mcq.option4 || "N/A"}</p>
          </div>
        </div>
        <p><strong>Correct Answer:</strong> <span style="color: var(--teal); font-weight: bold;">${
          answerDisplay || "N/A"
        }</span></p>
        <p><strong>Created At:</strong> ${
          mcq.createdAt ? new Date(mcq.createdAt).toLocaleDateString() : "N/A"
        }</p>
        <p><strong>Updated At:</strong> ${
          mcq.updatedAt ? new Date(mcq.updatedAt).toLocaleDateString() : "N/A"
        }</p>
      </div>
    `,
      iconColor: "var(--saffron)",
      confirmButtonText: "Close",
      confirmButtonColor: "var(--berry)",
      backdrop: "rgba(70, 69, 69, 0.7)",
      width: 700,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const hasMoreMcqs = data.length < totalRecords;

  // The next page arrives on scroll. The dialog body is what scrolls
  // when this list is embedded, so the observer finds its scrolling
  // ancestor rather than assuming the window.
  useEffect(() => {
    const node = mcqEndRef.current;
    if (!node || !hasMoreMcqs || loading) return undefined;
    let root = node.parentElement;
    while (root && root !== document.body) {
      const oy = window.getComputedStyle(root).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      root = root.parentElement;
    }
    if (root === document.body) root = null;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      // Modest, so a list shorter than its container does not keep the
      // sentinel in view and load page after page unprompted.
      { root, rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMoreMcqs, loading, data.length]);

  const handleSearch = (search) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };


  const indexedData = data.map((item, index) => ({
    ...item,
    index: (currentPage - 1) * recordsPerPage + index + 1,
  }));

  if (loading && !isInitialized) {
    return (
      <div
        className="pg-state"
      >
        <span className="pg-spinner" aria-hidden="true" />
        <p className="pg-state-text">Loading MCQs...</p>
      </div>
    );
  }

  return (
    <>
      <div className="mcq-bar">
        {!embedded && (
          <button type="button" className="pg-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            Back to courses
          </button>
        )}

        <input
          type="search"
          className="pg-input mcq-search"
          placeholder="Search questions..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          aria-label="Search questions"
        />

        {effectiveTrainerFilter && (
          <span className="pg-pill">Filtered by trainer</span>
        )}

        {hasPermission("course-wise-mcq", "add", "lms") && (
          <button
            type="button"
            className={showAddForm ? "pg-btn" : "pg-btn pg-btn--main"}
            onClick={showAddForm ? handleCancelForm : handleAddMcq}
          >
            {showAddForm ? (
              "Cancel"
            ) : (
              <>
                <Plus size={15} />
                Add MCQ
              </>
            )}
          </button>
        )}
      </div>

      {/* Add and edit are the same three questions, asked in a dialog
          over the list rather than pushed in above it. */}
      {showAddForm && (
        // Nested: this opens inside the course dialog, so it sizes and
        // positions against that panel rather than the viewport.
        <div
          className="pg-modal pg-modal--nested"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mcq-edit-title"
          onClick={handleCancelForm}
        >
          <div
            className="pg-modal-panel pg-modal-panel--nested pg-modal-panel--steady"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">
                  {editingMcq ? "Edit MCQ" : "Add MCQ"}
                </span>
                <h3 className="pg-modal-title" id="mcq-edit-title">
                  {MCQ_FORM_STEPS[formStep - 1].title}
                </h3>
                <p className="pg-modal-sub">
                  {MCQ_FORM_STEPS[formStep - 1].hint}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={handleCancelForm}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formNotice?.kind === "ok" ? (
                // Said in the dialog the work was done in; it closes itself
                // a few seconds later.
                <PopupDone
                  message={formNotice.text}
                  text="The question list behind this has been updated."
                  onClose={handleCancelForm}
                />
              ) : (
              <Formik
                enableReinitialize
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <form onSubmit={handleSubmit} className="mcq-form" noValidate>
                    {formNotice?.kind === "bad" && (
                      <p className="pg-notice pg-notice--bad" role="status">
                        {formNotice.text}
                      </p>
                    )}

                    <ol className="att-steps">
                      {MCQ_FORM_STEPS.map((s, i) => {
                        const n = i + 1;
                        const state =
                          n === formStep
                            ? "is-now"
                            : n < formStep
                              ? "is-done"
                              : "";
                        return (
                          <li
                            className={`att-step-tab ${state}`.trim()}
                            key={s.label}
                          >
                            <button
                              type="button"
                              disabled={n === formStep || (!editingMcq && n > formStep)}
                              onClick={() => setFormStep(n)}
                            >
                              <span className="att-step-n">{n}</span>
                              {s.label}
                            </button>
                          </li>
                        );
                      })}
                    </ol>

                    <div className={`att-stage is-${formPhase}`}>
                      <div className="att-stage-inner" key={formShownStep}>
                        {formShownStep === 1 && (
                          <section className="att-step">
                            <div className="pg-field">
                              <label className="pg-label" htmlFor="mcqe-question">
                                Question
                              </label>
                              <textarea
                                id="mcqe-question"
                                name="question"
                                className="pg-input mcq-textarea"
                                rows={4}
                                placeholder="Enter the question"
                                value={values.question}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.question && errors.question && (
                                <p className="att-error">{errors.question}</p>
                              )}
                            </div>
                          </section>
                        )}

                        {formShownStep === 2 && (
                          <section className="att-step">
                            <div className="mcq-options">
                              {[1, 2, 3, 4].map((n) => {
                                const key = `option${n}`;
                                return (
                                  <div className="pg-field" key={key}>
                                    <label
                                      className="pg-label"
                                      htmlFor={`mcqe-${key}`}
                                    >
                                      Option {n}
                                    </label>
                                    <input
                                      id={`mcqe-${key}`}
                                      name={key}
                                      type="text"
                                      className="pg-input"
                                      placeholder={`Enter option ${n}`}
                                      value={values[key]}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    {touched[key] && errors[key] && (
                                      <p className="att-error">{errors[key]}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="pg-field mcq-answer">
                              <label className="pg-label" htmlFor="mcqe-answer">
                                Correct answer
                              </label>
                              <select
                                id="mcqe-answer"
                                name="correctAnswer"
                                className="pg-select"
                                value={values.correctAnswer}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select the correct option</option>
                                {[1, 2, 3, 4].map((n) => (
                                  <option key={n} value={n}>
                                    {values[`option${n}`]
                                      ? `Option ${n} — ${values[`option${n}`]}`
                                      : `Option ${n}`}
                                  </option>
                                ))}
                              </select>
                              {touched.correctAnswer && errors.correctAnswer && (
                                <p className="att-error">
                                  {errors.correctAnswer}
                                </p>
                              )}
                            </div>
                          </section>
                        )}

                        {formShownStep === 3 && (
                          <section className="att-step">
                            <div className="pg-field">
                              <label className="pg-label">Trainer</label>
                              {isTrainer ? (
                                <input
                                  type="text"
                                  className="pg-input"
                                  value={getCurrentTrainerName()}
                                  readOnly
                                  disabled
                                />
                              ) : (
                                <NewCustomAsyncSelectDropdown
                                  options={trainerOptions}
                                  value={(() => {
                                    const hit = trainerOptions.find(
                                      (o) => o.value === values.selectedTrainer,
                                    );
                                    if (hit) return [hit];
                                    return selectedTrainerOption &&
                                      selectedTrainerOption.value ===
                                        values.selectedTrainer
                                      ? [selectedTrainerOption]
                                      : [];
                                  })()}
                                  onChange={(opts) => {
                                    const picked =
                                      Array.isArray(opts) && opts.length
                                        ? opts[opts.length - 1]
                                        : null;
                                    setSelectedTrainerOption(picked);
                                    setFieldValue(
                                      "selectedTrainer",
                                      picked?.value || "",
                                    );
                                  }}
                                  placeholder="Select trainer..."
                                  isMulti={false}
                                  isSearchable
                                  searchInControl={true}
                                  isLoading={trainerLoading}
                                  onMenuOpen={handleTrainerMenuOpen}
                                  onInputChange={handleTrainerSearch}
                                  onMenuScrollToBottom={handleTrainerScrollEnd}
                                  onRefresh={handleTrainerRefresh}
                                  loadingMessage="Loading trainers..."
                                  noOptionsMessage="No trainers available"
                                  noSearchResultsMessage="No trainers found"
                                />
                              )}
                              {touched.selectedTrainer &&
                                errors.selectedTrainer && (
                                  <p className="att-error">
                                    {errors.selectedTrainer}
                                  </p>
                                )}
                            </div>

                            <p className="att-note">
                              This question belongs to <b>{courseName}</b>.
                            </p>
                          </section>
                        )}
                      </div>
                    </div>

                    <div className="att-actions">
                      {formStep > 1 ? (
                        <button
                          type="button"
                          className="pg-btn"
                          onClick={() => setFormStep(formStep - 1)}
                        >
                          Back
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="pg-btn"
                          onClick={handleCancelForm}
                        >
                          Cancel
                        </button>
                      )}

                      {formStep < 3 ? (
                        <button
                          key="mcq-next"
                          type="button"
                          className="pg-btn pg-btn--main"
                          disabled={
                            !mcqStepComplete(formStep, values, errors, isAdmin)
                          }
                          onClick={() => setFormStep(formStep + 1)}
                        >
                          Continue
                        </button>
                      ) : (
                        <button
                          key="mcq-save"
                          type="button"
                          onClick={() => handleSubmit()}
                          className="pg-btn pg-btn--main"
                          disabled={
                            isSubmitting ||
                            !mcqStepComplete(1, values, errors, isAdmin) ||
                            !mcqStepComplete(2, values, errors, isAdmin) ||
                            !mcqStepComplete(3, values, errors, isAdmin)
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className="pg-spinner att-spinner"
                                aria-hidden="true"
                              />
                              {editingMcq ? "Updating..." : "Adding..."}
                            </>
                          ) : editingMcq ? (
                            "Update MCQ"
                          ) : (
                            "Add MCQ"
                          )}
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </Formik>
              )}
            </div>
          </div>
        </div>
      )}

      {data.length === 0 && !loading ? (
        <div className="pg-state">
          <p className="pg-state-title">No MCQs yet</p>
          <p className="pg-state-text">
            {effectiveTrainerFilter
              ? "No MCQs for the selected trainer in this course."
              : "Nothing has been added to this course yet."}
          </p>
          {hasPermission("course-wise-mcq", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleAddMcq}
            >
              Add the first MCQ
            </button>
          )}
        </div>
      ) : (
        /* The question on its own line, its answer and options beneath
           it. A table put the question in a column the width of one
           option, which is the one thing here that needs the room. No
           frame on the rows -- the hairline between them is enough. */
        <ol className="mcq-qlist">
          {indexedData.map((row) => {
            const options = [row.option1, row.option2, row.option3, row.option4];
            const correct = Number(row.correctAnswer);
            return (
              <li className="mcq-q" key={row.id}>
                <p className="mcq-q-text">
                  <span className="mcq-q-n">{row.index}</span>
                  {row.question || "Untitled question"}
                </p>

                <div className="mcq-q-meta">
                  {options.map((opt, i) => {
                    const n = i + 1;
                    if (!opt) return null;
                    return (
                      <span
                        className={`mcq-opt${correct === n ? " is-right" : ""}`}
                        key={n}
                      >
                        <b>{String.fromCharCode(64 + n)}</b>
                        {opt}
                      </span>
                    );
                  })}
                </div>

                <div className="mcq-q-actions">
                  {hasPermission("course-wise-mcq", "update", "lms") && (
                    <button
                      type="button"
                      className="pg-btn"
                      onClick={() => handleEditMcq(row.id)}
                    >
                      Edit
                    </button>
                  )}
                  {hasPermission("course-wise-mcq", "delete", "lms") && (
                    <button
                      type="button"
                      className="pg-btn pg-btn--danger"
                      onClick={() => handleDelete(row.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {data.length > 0 && (
        <div className="att-picklist-foot" ref={mcqEndRef}>
          {loadingMore ? (
            <>
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>Loading more questions...</span>
            </>
          ) : hasMoreMcqs ? (
            <span>
              {data.length} of {totalRecords} questions
            </span>
          ) : (
            <span>
              All {totalRecords} question{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default McqList;